import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import SEO from './SEO';
import Header from './Header';
import Footer from './Footer';
import { CARRIERS, SHARED_FEATURES } from '../data/carriers';
import { IMAGES } from '../Assets/Images/index';
import './CarrierPage.css';

function CarrierPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [trackingNumber, setTrackingNumber] = useState('');
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const carrier = CARRIERS.find((c) => c.slug === slug);

  if (!carrier) {
    return (
      <div className="carrier-page">
        <Header />
        <div className="carrier-not-found">
          <h1>Carrier Not Found</h1>
          <p>We couldn't find a carrier page for "<strong>{slug}</strong>".</p>
          <Link to="/carriers" className="back-link">View All Carriers</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const logo = carrier.logoKey ? IMAGES[carrier.logoKey] : null;

  const handleTrack = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!trackingNumber.trim()) return;
    const params = new URLSearchParams();
    params.set('tracking', trackingNumber.trim());
    navigate(`/results?${params.toString()}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleTrack();
  };

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div className="carrier-page">
      <SEO
        title={`${carrier.name} Container Tracking`}
        description={`Track ${carrier.name} containers, bookings, and Bills of Lading in real time. View container prefixes, SCAC codes, and get delay alerts for all ${carrier.name} shipments.`}
        canonicalPath={`/carriers/${carrier.slug}`}
      />
      <Header />

      {/* ── HERO ── */}
      <section className="cp-hero">
        <div className="cp-hero-map-bg" style={{ backgroundImage: "url('/map.png')" }} />
        <div className="cp-hero-inner">
          <div className="cp-hero-text">
            {logo && (
              <img src={logo} alt={`${carrier.name} logo`} className="cp-hero-logo" />
            )}
            <h1 className="cp-hero-title">{carrier.heroTitle}</h1>
            <p className="cp-hero-subtitle">
              Real-time tracking for all {carrier.name} containers, bookings, and Bills of Lading.
            </p>
            <div className="cp-search-bar-container">
              <div className="cp-search-bar">
                <input
                  className="cp-search-input"
                  placeholder="Enter Tracking Number"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <button className="cp-search-btn" onClick={handleTrack}>
                  🔍
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BREADCRUMB ── */}
      <div className="cp-breadcrumb">
        <Link to="/">Home</Link> / <Link to="/carriers">Carriers</Link> / <span>{carrier.name}</span>
      </div>

      {/* ── ABOUT ── */}
      <section className="cp-section cp-about">
        <div className="cp-section-inner">
          <div className="cp-about-text">
            <h2 className="cp-section-title">About {carrier.name}</h2>
            {carrier.aboutText.map((para, i) => (
              <p key={i} className="cp-about-para">{para}</p>
            ))}
          </div>
          {logo && (
            <div className="cp-about-logo-wrap">
              <img src={logo} alt={`${carrier.name} logo`} className="cp-about-logo" />
            </div>
          )}
        </div>
      </section>

      {/* ── TECHNICAL DATA ── */}
      <section className="cp-section cp-technical">
        <div className="cp-section-inner">
          <h2 className="cp-section-title">Technical Data</h2>
          {carrier.scac && (
            <div className="cp-scac-row">
              <span className="cp-scac-label">SCAC Code:</span>
              <span className="cp-scac-badge">{carrier.scac}</span>
              <span className="cp-scac-desc">Standard Carrier Alpha Code used in shipping documentation and EDI</span>
            </div>
          )}
          <h3 className="cp-prefix-heading">Container Prefix Codes ({carrier.prefixCodes.length})</h3>
          <div className="cp-prefix-grid">
            {carrier.prefixCodes.map((code, i) => (
              <span key={i} className="cp-prefix-badge">{code}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW-TO GUIDE ── */}
      <section className="cp-section cp-howto">
        <div className="cp-section-inner">
          <h2 className="cp-section-title">How to Track a {carrier.name} Shipment</h2>
          <div className="cp-steps">
            <div className="cp-step">
              <span className="cp-step-number">1</span>
              <div className="cp-step-content">
                <h4>Find Your Reference Number</h4>
                <p>Locate your {carrier.name} container number (e.g., {carrier.prefixCodes[0]}1234567), booking number, or Bill of Lading from your shipping documents or confirmation email.</p>
              </div>
            </div>
            <div className="cp-step">
              <span className="cp-step-number">2</span>
              <div className="cp-step-content">
                <h4>Enter It Above</h4>
                <p>Paste or type your reference number into the search bar at the top of this page and click "Track Shipment".</p>
              </div>
            </div>
            <div className="cp-step">
              <span className="cp-step-number">3</span>
              <div className="cp-step-content">
                <h4>View Real-Time Status</h4>
                <p>Instantly see your container's current location, vessel name, ETA, port events, and transit history — all in one place.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURE COMPARISON ── */}
      <section className="cp-section cp-features">
        <div className="cp-section-inner">
          <h2 className="cp-section-title">Benefits of {carrier.name} Tracking on RideLink</h2>
          <div className="cp-table-wrapper">
            <table className="cp-feature-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>{carrier.name} Website</th>
                  <th>RideLink Tracking</th>
                </tr>
              </thead>
              <tbody>
                {SHARED_FEATURES.map((row, i) => (
                  <tr key={i}>
                    <td>{row.feature}</td>
                    <td className={row.carrierSite ? 'cp-check' : 'cp-cross'}>
                      {row.carrierSite ? '✓' : '✗'}
                    </td>
                    <td className={row.shiptrack ? 'cp-check' : 'cp-cross'}>
                      {row.shiptrack ? '✓' : '✗'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── LATEST DISCHARGED SHIPMENTS ── */}
      <section className="cp-section cp-shipments">
        <div className="cp-section-inner">
          <h2 className="cp-section-title">Latest {carrier.name} Discharged Shipments</h2>
          <div className="cp-table-wrapper">
            <table className="cp-shipments-table">
              <thead>
                <tr>
                  <th>From</th>
                  <th>To</th>
                  <th>Total Transit Time</th>
                  <th>Transits</th>
                </tr>
              </thead>
              <tbody>
                {carrier.sampleShipments.map((s, i) => (
                  <tr key={i}>
                    <td><span className="cp-flag">{s.fromFlag}</span> {s.from}</td>
                    <td><span className="cp-flag">{s.toFlag}</span> {s.to}</td>
                    <td><span className="cp-transit-days">{s.transitDays} Days</span></td>
                    <td>{s.transits} {s.transits === 1 ? 'Transshipment' : s.transits === 0 ? 'Direct' : 'Transshipments'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="cp-shipments-note">* Sample data shown for illustration. Live data is available after sign-in.</p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="cp-section cp-faq">
        <div className="cp-section-inner">
          <h2 className="cp-section-title">Frequently Asked Questions</h2>
          <div className="cp-faq-list">
            {carrier.faq.map((item, i) => (
              <div key={i} className={`cp-faq-item ${openFaqIndex === i ? 'cp-faq-open' : ''}`}>
                <button className="cp-faq-question" onClick={() => toggleFaq(i)}>
                  <span>{item.question}</span>
                  <span className="cp-faq-icon">{openFaqIndex === i ? '−' : '+'}</span>
                </button>
                <div className="cp-faq-answer">
                  <p>{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default CarrierPage;
